import {
	createContext,
	useContext,
	useEffect,
	useState,
	useCallback,
	useRef,
	type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { ChatMessageDirection, ChatMessageType, ChatRoomStatus } from '@/schema'
import {
	isFCMSupported,
	setupForegroundMessageListener,
	notifyServiceWorkerOfSseEvent,
} from '@/modules/notification/utils/firebase'
import { useFCMToken } from '@/modules/notification/hooks/useFCMToken'
import useQueryGetAdminAssets from '@/modules/admin-assets/api/hooks/useQueryGetAdminAssets'

import { chatKeys } from '../keys'
import type {
	ChatSseEvent,
	ChatNewMessagePayload,
	ChatRoomUpdatedPayload,
	ChatRoomListChangedPayload,
	ChatTagUpdatedPayload,
	ChatAssigneeUpdatedPayload,
	ChatMessageReadPayload,
	ChatMessageConfirmedPayload,
	ChatRoomBannedPayload,
	ChatRoomPinnedPayload,
	ChatMessagePinnedPayload,
	ChatBroadcastSentPayload,
	ChatAiDraftPayload,
	ChatAiTypingStartPayload,
	ChatAiTypingStopPayload,
	WithdrawPendingPayload,
	DepositPendingPayload,
	ChatRoomResponse,
	ChatMessageResponse,
	ChatRoomListResponse,
	ChatMessageListResponse,
} from './types'

type InfiniteRoomData = {
	pages: ChatRoomListResponse[]
	pageParams: (string | undefined)[]
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export type NewMessageCallbackPayload = {
	roomId: string
	message: ChatMessageResponse
}

interface ChatSseContextValue {
	status: ConnectionStatus
	connectionCount: number | null
	currentRoomId: string | null
	setCurrentRoomId: (roomId: string | null) => void
	// Jump to message cursor - used for navigating to specific messages
	jumpToMessageId: string | null
	setJumpToMessageId: (messageId: string | null) => void
	// AI typing state - which room AI is currently typing in
	aiTypingRoomId: string | null
	onMessageConfirmed: (
		callback: (payload: ChatMessageConfirmedPayload) => void,
	) => () => void
	onNewMessage: (
		callback: (payload: NewMessageCallbackPayload) => void,
	) => () => void
}

const ChatSseContext = createContext<ChatSseContextValue | null>(null)

interface ChatSseProviderProps {
	children: ReactNode
}

export function ChatSseProvider({ children }: ChatSseProviderProps) {
	const [status, setStatus] = useState<ConnectionStatus>('disconnected')
	const [connectionCount, setConnectionCount] = useState<number | null>(null)
	const [currentRoomId, setCurrentRoomId] = useState<string | null>(null)
	const [jumpToMessageId, setJumpToMessageId] = useState<string | null>(null)
	const [aiTypingRoomId, setAiTypingRoomId] = useState<string | null>(null)
	const queryClient = useQueryClient()
	const eventSourceRef = useRef<EventSource | null>(null)
	const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const currentRoomIdRef = useRef<string | null>(null)
	const messageConfirmedCallbacksRef = useRef<
		Set<(payload: ChatMessageConfirmedPayload) => void>
	>(new Set())
	const newMessageCallbacksRef = useRef<
		Set<(payload: NewMessageCallbackPayload) => void>
	>(new Set())

	// Fetch admin asset config for custom notification sound
	const { data: adminAssets } = useQueryGetAdminAssets()
	const adminAssetsRef = useRef(adminAssets)
	useEffect(() => {
		adminAssetsRef.current = adminAssets
	}, [adminAssets])

	// Keep ref in sync with state for use in SSE event handlers
	useEffect(() => {
		currentRoomIdRef.current = currentRoomId
	}, [currentRoomId])

	useEffect(() => {
		setJumpToMessageId(null)
	}, [currentRoomId])

	// Helper to update a room in infinite query cache
	const updateRoomInCache = useCallback(
		(roomId: string, updater: (room: ChatRoomResponse) => ChatRoomResponse) => {
			queryClient.setQueriesData<InfiniteRoomData>(
				{ queryKey: chatKeys.rooms() },
				(old) => {
					if (!old?.pages?.length) return old
					return {
						...old,
						pages: old.pages.map((page) => ({
							...page,
							items: page.items.map((room) =>
								room.id === roomId ? updater(room) : room,
							),
						})),
					}
				},
			)
		},
		[queryClient],
	)

	// Refetch queries on reconnect to ensure data consistency
	const refetchOnReconnect = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
		if (currentRoomId) {
			queryClient.invalidateQueries({
				queryKey: chatKeys.messages(currentRoomId),
			})
			queryClient.invalidateQueries({ queryKey: chatKeys.room(currentRoomId) })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryClient])

	// Helper to add a message to a room's message cache
	type InfiniteMessageData = {
		pages: ChatMessageListResponse[]
		pageParams: (string | undefined)[]
	}

	const addMessageToRoomCache = useCallback(
		(
			roomId: string,
			message: ChatMessageResponse,
			options?: { addUnreadDivider?: boolean },
		) => {
			const { addUnreadDivider = false } = options ?? {}

			queryClient.setQueriesData<InfiniteMessageData>(
				{ queryKey: chatKeys.messages(roomId) },
				(old) => {
					if (!old || !old.pages || old.pages.length === 0) return old

					// Check if message already exists in any page
					const existsById = old.pages.some((page) =>
						page.items.some((m) => m.id === message.id),
					)
					if (existsById) return old

					// Only add message if we're viewing the latest messages (first page has no prevCursor)
					const firstPage = old.pages[0]
					const isViewingLatest = !firstPage?.pagination?.prevCursor
					if (!isViewingLatest) return old

					// Check if unread divider should be added
					let unreadDividerMessage: ChatMessageResponse | null = null
					if (
						addUnreadDivider &&
						message.direction === ChatMessageDirection.INCOMING
					) {
						const unreadDividerId = `unread-divider-${roomId}`
						const hasUnreadDivider = old.pages.some((page) =>
							page.items.some((m) => m.id === unreadDividerId),
						)
						if (!hasUnreadDivider) {
							unreadDividerMessage = {
								id: unreadDividerId,
								chatRoomId: roomId,
								direction: ChatMessageDirection.INCOMING,
								messageType: ChatMessageType.SYSTEM,
								text: 'ข้อความที่ยังไม่อ่าน',
								isPinned: false,
								usedReplyToken: false,
								isRead: false,
								createdAt: message.createdAt,
							}
						}
					}

					// Add to the first page (newest messages are at the start)
					return {
						...old,
						pages: old.pages.map((page, index) => {
							if (index === 0) {
								const newItems = unreadDividerMessage
									? [message, unreadDividerMessage, ...page.items]
									: [message, ...page.items]
								return { ...page, items: newItems }
							}
							return page
						}),
					}
				},
			)
		},
		[queryClient],
	)

	// Initialize SSE connection
	useEffect(() => {
		const token = localStorage.getItem('token')
		if (!token) {
			setStatus('error')
			return
		}

		const connect = () => {
			setStatus('connecting')

			const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
			const sseUrl = `${baseUrl}/admin/chat/events?token=${encodeURIComponent(token)}`

			const es = new EventSource(sseUrl)
			eventSourceRef.current = es

			es.onopen = () => {
				setStatus('connected')
				// Refetch data on reconnect to ensure consistency
				refetchOnReconnect()
			}

			// Handle chat:new_message event
			es.addEventListener('chat:new_message', (e) => {
				console.log('chat:new_message')
				const event: ChatSseEvent<ChatNewMessagePayload> = JSON.parse(e.data)

				// Notify service worker of this event for push notification deduplication
				notifyServiceWorkerOfSseEvent(event.id)

				const payload = event.data
				const message = payload.message as ChatMessageResponse

				// Add message to room's message cache
				addMessageToRoomCache(payload.roomId, message, {
					addUnreadDivider: true,
				})

				// Update room in cache with new message info
				updateRoomInCache(payload.roomId, (room) => ({
					...room,
					lastMessageAt: message.createdAt,
					// Only increment unread for incoming messages
					unreadCount:
						message.direction === 'INCOMING'
							? room.unreadCount + 1
							: room.unreadCount,
				}))

				// Invalidate room list to re-fetch with correct order from server
				// This is simpler than client-side re-sorting with paginated infinite queries
				queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })

				// Invalidate sidebar unread count for incoming messages
				if (message.direction === 'INCOMING') {
					queryClient.invalidateQueries({ queryKey: chatKeys.unreadCount() })
				}

				// Play notification sound for incoming messages (from customer, not admin)
				if (message.direction === 'INCOMING') {
					const audio = new Audio('/line-bell.mp3')
					audio.volume = 0.5
					audio.play().catch(() => {
						// Ignore autoplay errors (browser policy)
					})

					const room = queryClient.getQueryData<ChatRoomResponse>(
						chatKeys.room(payload.roomId),
					)

					// Show browser notification if tab is hidden and permission granted
					if (
						'Notification' in window &&
						document.hidden &&
						Notification.permission === 'granted'
					) {
						const notificationBody = message.text || '[รูปภาพ/สติกเกอร์]'
						const notification = new Notification(
							'มีข้อความใหม่' +
								`${room?.lineDisplayName ? `จาก ${room.lineDisplayName}` : ''}`,
							{
								body: notificationBody,
								icon: '/favicon.ico',
								tag: `chat-${payload.roomId}`,
							},
						)

						notification.onclick = () => {
							window.focus()
							window.location.href = `/operator/chat/${payload.roomId}`
						}
					}
				}

				// Notify all registered new message callbacks
				newMessageCallbacksRef.current.forEach((callback) => {
					callback({ roomId: payload.roomId, message })
				})
			})

			// Handle chat:room_updated event
			es.addEventListener('chat:room_updated', (e) => {
				const event: ChatSseEvent<ChatRoomUpdatedPayload> = JSON.parse(e.data)
				const payload = event.data
				const room = payload.room as ChatRoomResponse

				// Update specific room in cache
				queryClient.setQueryData<ChatRoomResponse>(
					chatKeys.room(payload.roomId),
					room,
				)

				// If room status changed to CLOSED (unfollow), invalidate lists
				// so the room is removed from active list and added to closed list
				if (room.status === ChatRoomStatus.CLOSED) {
					queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
					queryClient.invalidateQueries({ queryKey: chatKeys.closedRooms() })
					return
				}

				// Update room in list cache
				updateRoomInCache(payload.roomId, () => room)
			})

			// Handle chat:room_list_changed event
			es.addEventListener('chat:room_list_changed', (e) => {
				const event: ChatSseEvent<ChatRoomListChangedPayload> = JSON.parse(
					e.data,
				)
				const payload = event.data

				// For status changes (e.g., unfollow -> CLOSED), invalidate all room lists
				// so the room moves between lists (active <-> closed)
				// Note: 'status_changed' may not be in schema yet, cast to string for comparison
				if ((payload.reason as string) === 'status_changed') {
					queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
					queryClient.invalidateQueries({ queryKey: chatKeys.closedRooms() })
					return
				}

				// Check if room exists in cache
				const roomsData = queryClient.getQueryData<InfiniteRoomData>(
					chatKeys.rooms(),
				)
				const roomExists = roomsData?.pages?.some((page) =>
					page.items.some((room) => room.id === payload.roomId),
				)

				if (!roomExists) {
					// Room not in cache (new room), refetch the list
					queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
					return
				}

				// Invalidate room detail to refetch full data from server
				queryClient.invalidateQueries({
					queryKey: chatKeys.room(payload.roomId),
				})

				// Update room in list with lightweight data
				updateRoomInCache(payload.roomId, (room) => ({
					...room,
					lineDisplayName: payload.lineDisplayName ?? room.lineDisplayName,
					linePictureUrl: payload.linePictureUrl ?? room.linePictureUrl,
					unreadCount: payload.unreadCount,
					lastMessageAt: payload.lastMessageAt,
				}))
			})

			// Handle chat:tag_updated event
			es.addEventListener('chat:tag_updated', (e) => {
				const event: ChatSseEvent<ChatTagUpdatedPayload> = JSON.parse(e.data)
				const payload = event.data

				// Update room's tags in cache
				queryClient.setQueryData<ChatRoomResponse>(
					chatKeys.room(payload.roomId),
					(old) => {
						if (!old) return old
						return {
							...old,
							tags: payload.tags as ChatRoomResponse['tags'],
						}
					},
				)

				// Update room in list
				updateRoomInCache(payload.roomId, (room) => ({
					...room,
					tags: payload.tags as ChatRoomResponse['tags'],
				}))
			})

			// Handle chat:assignee_updated event
			es.addEventListener('chat:assignee_updated', (e) => {
				const event: ChatSseEvent<ChatAssigneeUpdatedPayload> = JSON.parse(
					e.data,
				)

				// Notify service worker of this event for push notification deduplication
				notifyServiceWorkerOfSseEvent(event.id)

				const payload = event.data

				// Invalidate room detail to refetch full data from server
				queryClient.invalidateQueries({
					queryKey: chatKeys.room(payload.roomId),
				})

				// Update room in list cache (only update assignedOperator field)
				updateRoomInCache(payload.roomId, (room) => ({
					...room,
					assignedOperator: payload.assignedOperatorId
						? {
								id: payload.assignedOperatorId,
								username: payload.assignedOperatorName || '',
							}
						: undefined,
				}))

				// Invalidate room list to update filters
				queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
			})

			// Handle chat:message_read event
			es.addEventListener('chat:message_read', (e) => {
				const event: ChatSseEvent<ChatMessageReadPayload> = JSON.parse(e.data)
				const payload = event.data

				// Update messages read status in cache
				queryClient.setQueryData<ChatMessageListResponse>(
					chatKeys.messages(payload.roomId),
					(old) => {
						if (!old) return old
						return {
							...old,
							items: old.items.map((msg) =>
								payload.messageIds.includes(msg.id)
									? {
											...msg,
											isRead: true,
											readAt: payload.readAt,
											readByOperator: {
												id: payload.readByOperatorId,
												username: payload.readByOperatorName,
											},
										}
									: msg,
							),
						}
					},
				)

				// Update room's unread count
				queryClient.invalidateQueries({
					queryKey: chatKeys.room(payload.roomId),
				})
				queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
				// Invalidate sidebar unread count
				queryClient.invalidateQueries({ queryKey: chatKeys.unreadCount() })
			})

			// Handle chat:message_confirmed event (for optimistic updates - only update pending status)
			es.addEventListener('chat:message_confirmed', (e) => {
				const event: ChatSseEvent<ChatMessageConfirmedPayload> = JSON.parse(
					e.data,
				)
				const payload = event.data

				// Notify all registered callbacks (for pending message state updates)
				// This updates the "sending" state to "sent" in the UI
				messageConfirmedCallbacksRef.current.forEach((callback) => {
					callback(payload)
				})

				// Note: We don't add/update messages here anymore
				// chat:new_message handles adding all messages to cache
			})

			// Handle chat:room_banned event
			es.addEventListener('chat:room_banned', (e) => {
				const event: ChatSseEvent<ChatRoomBannedPayload> = JSON.parse(e.data)
				const payload = event.data

				// Update room's banned status in cache
				queryClient.setQueryData<ChatRoomResponse>(
					chatKeys.room(payload.roomId),
					(old) => {
						if (!old) return old
						return {
							...old,
							isBanned: payload.isBanned,
							banReason: payload.reason,
						}
					},
				)

				// Update room in list cache
				updateRoomInCache(payload.roomId, (room) => ({
					...room,
					isBanned: payload.isBanned,
					banReason: payload.reason,
				}))

				// Invalidate room lists to update filters (banned rooms list, etc.)
				queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
				queryClient.invalidateQueries({ queryKey: chatKeys.bannedRooms() })
				// Invalidate sidebar unread count (banned rooms are excluded from count)
				queryClient.invalidateQueries({ queryKey: chatKeys.unreadCount() })
			})

			// Handle chat:room_pinned event
			es.addEventListener('chat:room_pinned', (e) => {
				const event: ChatSseEvent<ChatRoomPinnedPayload> = JSON.parse(e.data)
				const payload = event.data

				// Update room's pinned status in cache
				queryClient.setQueryData<ChatRoomResponse>(
					chatKeys.room(payload.roomId),
					(old) => {
						if (!old) return old
						return {
							...old,
							isPinned: payload.isPinned,
						}
					},
				)

				// Update room in list cache
				updateRoomInCache(payload.roomId, (room) => ({
					...room,
					isPinned: payload.isPinned,
				}))

				// Invalidate room list to update order (pinned rooms appear first)
				queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
			})

			// Handle chat:message_pinned event
			es.addEventListener('chat:message_pinned', (e) => {
				const event: ChatSseEvent<ChatMessagePinnedPayload> = JSON.parse(e.data)
				const payload = event.data

				// Update message's pinned status in messages cache
				type InfiniteMessageData = {
					pages: ChatMessageListResponse[]
					pageParams: (string | undefined)[]
				}

				queryClient.setQueriesData<InfiniteMessageData>(
					{ queryKey: chatKeys.messages(payload.roomId) },
					(old) => {
						if (!old?.pages?.length) return old
						return {
							...old,
							pages: old.pages.map((page) => ({
								...page,
								items: page.items.map((msg) =>
									msg.id === payload.messageId
										? { ...msg, isPinned: payload.isPinned }
										: msg,
								),
							})),
						}
					},
				)

				// Invalidate room detail to update pinned messages list
				queryClient.invalidateQueries({
					queryKey: chatKeys.room(payload.roomId),
				})

				queryClient.invalidateQueries({
					queryKey: chatKeys.pinnedMessages(payload.roomId),
				})
			})

			// Handle chat:broadcast_sent event (notification that messages were sent to multiple rooms)
			es.addEventListener('chat:broadcast_sent', (e) => {
				const event: ChatSseEvent<ChatBroadcastSentPayload> = JSON.parse(e.data)
				const payload = event.data

				console.log(
					`[SSE] Broadcast sent: isAllRooms=${payload.isAllRooms}, totalRooms=${payload.totalRooms}`,
				)

				// Check if current room is affected
				const activeRoomId = currentRoomIdRef.current
				const isCurrentRoomAffected =
					payload.isAllRooms ||
					(activeRoomId && payload.roomIds.includes(activeRoomId))

				if (isCurrentRoomAffected && activeRoomId) {
					// Add the message to the current room's message list
					const messageForRoom: ChatMessageResponse = {
						...payload.message,
						chatRoomId: activeRoomId,
					}
					addMessageToRoomCache(activeRoomId, messageForRoom)
				}

				// Invalidate room list to refresh with new messages
				queryClient.invalidateQueries({ queryKey: chatKeys.rooms() })
				queryClient.invalidateQueries({ queryKey: chatKeys.analytics() })
			})

			es.addEventListener('chat:ai_draft', (e) => {
				const event: ChatSseEvent<ChatAiDraftPayload> = JSON.parse(e.data)
				const payload = event.data

				if (currentRoomIdRef.current !== payload.roomId) return

				queryClient.setQueryData(chatKeys.aiDraft(payload.roomId), payload)
			})

			es.addEventListener('chat:ai_typing_start', (e) => {
				const event: ChatSseEvent<ChatAiTypingStartPayload> = JSON.parse(e.data)
				setAiTypingRoomId(event.data.roomId)
			})

			es.addEventListener('chat:ai_typing_stop', (e) => {
				const event: ChatSseEvent<ChatAiTypingStopPayload> = JSON.parse(e.data)
				setAiTypingRoomId((prev) => (prev === event.data.roomId ? null : prev))
			})

			// Handle withdraw:pending event (notification that a withdrawal needs approval)
			es.addEventListener('withdraw:pending', (e) => {
				const event: ChatSseEvent<WithdrawPendingPayload> = JSON.parse(e.data)

				// Notify service worker of this event for push notification deduplication
				notifyServiceWorkerOfSseEvent(event.id)

				const payload = event.data

				console.log(
					`[SSE] Withdraw pending: txId=${payload.txId}, amount=${payload.amount}`,
				)

				// Play withdraw pending sound
				const audio = new Audio(
					adminAssetsRef.current?.withdrawSoundUrl ?? '/withdraw-pending.mp3',
				)
				audio.volume = 0.5
				audio.play().catch(() => {
					// Ignore autoplay errors (browser policy)
				})

				// Show toast notification with no timeout and action button
				toast('มีรายการถอนรอดำเนินการ', {
					description: `฿${payload.amount.toLocaleString()} - ${payload.playerPhone}`,
					duration: Infinity,
					action: {
						label: 'ดูรายการถอน',
						onClick: () => {
							window.location.href = `/operator/transactions/withdraws/${payload.txId}`
						},
					},
				})

				// Show browser notification if tab is hidden and permission granted
				if (
					'Notification' in window &&
					document.hidden &&
					Notification.permission === 'granted'
				) {
					const notification = new Notification('มีรายการถอนรอดำเนินการ', {
						body: `฿${payload.amount.toLocaleString()} - ${payload.playerPhone}`,
						icon: '/favicon.ico',
						tag: `withdraw-${payload.txId}`,
					})
					notification.onclick = () => {
						window.focus()
						window.location.href = `/operator/transactions/withdraws/${payload.txId}`
					}
				}
			})

			// Handle deposit:pending event (notification that a manual deposit needs approval)
			es.addEventListener('deposit:pending', (e) => {
				const event: ChatSseEvent<DepositPendingPayload> = JSON.parse(e.data)

				notifyServiceWorkerOfSseEvent(event.id)

				const payload = event.data

				console.log(
					`[SSE] Deposit pending: txId=${payload.txId}, amount=${payload.amount}`,
				)

				const audio = new Audio(
					adminAssetsRef.current?.withdrawSoundUrl ?? '/withdraw-pending.mp3',
				)
				audio.volume = 0.5
				audio.play().catch(() => {})

				toast('มีรายการฝากเงินรอดำเนินการ', {
					description: `฿${payload.amount.toLocaleString()} - ${payload.playerPhone}`,
					duration: Infinity,
					action: {
						label: 'ดูรายการฝาก',
						onClick: () => {
							window.location.href = '/operator/transactions/deposits'
						},
					},
				})

				if (
					'Notification' in window &&
					document.hidden &&
					Notification.permission === 'granted'
				) {
					const notification = new Notification('มีรายการฝากเงินรอดำเนินการ', {
						body: `฿${payload.amount.toLocaleString()} - ${payload.playerPhone}`,
						icon: '/favicon.ico',
						tag: `deposit-${payload.txId}`,
					})
					notification.onclick = () => {
						window.focus()
						window.location.href = '/operator/transactions/deposits'
					}
				}
			})

			// Handle keepalive ping - extract connection count from Redis
			es.addEventListener('ping', (e) => {
				try {
					const event = JSON.parse(e.data)
					// Backend sends connectionCount in ping event data from Redis
					if (event.data && typeof event.data.connectionCount === 'number') {
						setConnectionCount(event.data.connectionCount)
					}
				} catch {
					// Ignore parse errors for backward compatibility
				}
			})

			es.onerror = () => {
				setStatus('disconnected')
				es.close()
				eventSourceRef.current = null

				// Auto-reconnect after 3 seconds
				reconnectTimeoutRef.current = setTimeout(() => {
					connect()
				}, 3000)
			}
		}

		connect()

		return () => {
			if (eventSourceRef.current) {
				eventSourceRef.current.close()
				eventSourceRef.current = null
			}
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current)
				reconnectTimeoutRef.current = null
			}
		}
	}, [queryClient, refetchOnReconnect, updateRoomInCache])

	// Register callback for message confirmed events
	const onMessageConfirmed = useCallback(
		(callback: (payload: ChatMessageConfirmedPayload) => void) => {
			messageConfirmedCallbacksRef.current.add(callback)
			return () => {
				messageConfirmedCallbacksRef.current.delete(callback)
			}
		},
		[],
	)

	// Register callback for new message events
	const onNewMessage = useCallback(
		(callback: (payload: NewMessageCallbackPayload) => void) => {
			newMessageCallbacksRef.current.add(callback)
			return () => {
				newMessageCallbacksRef.current.delete(callback)
			}
		},
		[],
	)

	// FCM token registration
	const { requestPermissionAndRegisterToken } = useFCMToken()
	const fcmInitializedRef = useRef(false)

	// Register FCM token and setup foreground message listener
	useEffect(() => {
		if (fcmInitializedRef.current) return
		fcmInitializedRef.current = true

		// Request notification permission and register FCM token
		if (isFCMSupported()) {
			requestPermissionAndRegisterToken()
				.then((token) => {
					if (token) {
						console.log('FCM token registered successfully')
					}
				})
				.catch((error) => {
					console.error('Failed to register FCM token:', error)
				})

			// Setup foreground message listener
			// When app is in foreground, we suppress push notifications
			// since SSE will handle real-time updates
			const unsubscribe = setupForegroundMessageListener((payload) => {
				console.log('Foreground FCM message received (suppressed):', payload)
				// We don't show notification here since SSE handles it
				// This just logs for debugging
			})

			return () => {
				unsubscribe()
			}
		} else {
			// Fallback to browser notifications for unsupported platforms
			if ('Notification' in window && Notification.permission !== 'granted') {
				Notification.requestPermission().then((permission) => {
					console.log('Notification permission:', permission)
				})
			}
		}
	}, [requestPermissionAndRegisterToken])

	return (
		<ChatSseContext.Provider
			value={{
				status,
				connectionCount,
				currentRoomId,
				setCurrentRoomId,
				jumpToMessageId,
				setJumpToMessageId,
				aiTypingRoomId,
				onMessageConfirmed,
				onNewMessage,
			}}
		>
			{children}
		</ChatSseContext.Provider>
	)
}

export function useChatSse() {
	const context = useContext(ChatSseContext)
	if (!context) {
		throw new Error('useChatSse must be used within a ChatSseProvider')
	}
	return context
}

/**
 * Optional hook that returns null if not within ChatSseProvider
 * Use this in components that may or may not be wrapped by ChatSseProvider
 */
export function useChatSseOptional() {
	return useContext(ChatSseContext)
}
